import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth, roleSatisfies, type UserRole } from '@/contexts/AuthContext';

interface Props {
  children: React.ReactNode;
  requiredRole?: UserRole;
  loginPath: string;
}

/**
 * Pilot-grade route guard.
 *  - while AuthContext is hydrating from /api/auth/me, shows an a11y loading state
 *  - if no session (or refresh failed → tokens cleared) → redirect to loginPath,
 *    preserving the original path in router state for post-login bounce-back
 *  - if a session exists but the role does not satisfy the required role,
 *    shows an unauthorized state (does NOT silently redirect)
 */
const ProtectedRoute: React.FC<Props> = ({ children, requiredRole, loginPath }) => {
  const { user, role, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div
        role="status"
        aria-live="polite"
        className="min-h-screen flex items-center justify-center bg-eds-bg"
      >
        <span className="sr-only">Verifying your session</span>
        <div className="text-eds-muted-fg text-sm">Checking session…</div>
      </div>
    );
  }

  if (!user) {
    return (
      <Navigate
        to={loginPath}
        state={{ from: location.pathname + location.search, reason: 'unauthenticated' }}
        replace
      />
    );
  }

  if (requiredRole && !roleSatisfies(role, requiredRole)) {
    return (
      <div
        role="alert"
        className="min-h-screen flex items-center justify-center bg-eds-bg p-6"
      >
        <div className="max-w-md text-center bg-eds-surface border border-eds-border rounded-eds-lg p-8 shadow-eds-sm">
          <h2 className="text-xl font-bold text-eds-fg">Access denied</h2>
          <p className="text-eds-muted-fg mt-2">
            Your account role{' '}
            <span className="font-mono">{role ?? 'unknown'}</span>{' '}
            cannot access this area.
          </p>
          <p className="text-xs text-eds-muted-fg mt-3">
            If you believe this is an error, contact a GIISDEC administrator.
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;
